import { useCallback, useEffect, useState } from 'react';
import ga4 from 'react-ga4';
import type { IAnalyticsProps } from './Analytics.interface';
import CookieView from './CookieView';
import settings, { UserCookieStateValue } from './settings';
import { generateTranslatedText } from './util';

const DEFAULT_PRIVACY_POLICY_URL = 'https://www.ats-heritage.co.uk/privacy-policy';
const DEFAULT_TRACKING_ID = 'G-QRCSFP10H5';

function Analytics({ storage, logger, env, language, translater, manifest }: IAnalyticsProps) {
    const privacyPolicyHref = env.getString('privacy-policy-url', DEFAULT_PRIVACY_POLICY_URL);

    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [shouldTrack, setShouldTrack] = useState<boolean>(false);
    const [hasInitializedPageTracker, setHasInitializedPageTracker] = useState<boolean>(false);

    useEffect(() => {
        if (!storage) {
            return;
        }
        settings
            .UserCookieState(storage)
            .then(cookieState => {
                setShowDialog(cookieState === UserCookieStateValue.Unset);
                setShouldTrack(cookieState === UserCookieStateValue.Accepted);
            })
            .catch((error: Error) => {
                logger.error(`Failed to set cookie state with error: ${JSON.stringify(error)}`);
            });
    }, [storage, logger]);

    const onAccept = useCallback(() => {
        if (!storage) {
            return;
        }
        settings.SetUserCookieState(storage, UserCookieStateValue.Accepted).catch(error => {
            logger.error(`Failed to set cookie state with error: ${JSON.stringify(error)}`);
        });
        setShouldTrack(true);
        setShowDialog(false);
    }, [storage, logger]);

    const onDecline = useCallback(() => {
        if (!storage) {
            return;
        }
        settings
            .SetUserCookieState(storage, UserCookieStateValue.Accepted)
            .catch((error: Error) => {
                logger.error(`Failed to set cookie state with error: ${JSON.stringify(error)}`);
            });
        setShouldTrack(false);
        setShowDialog(false);
    }, [logger, storage]);

    useEffect(() => {
        if (!shouldTrack) {
            return;
        }

        if (!hasInitializedPageTracker) {
            const trackingId = env.getString('google-analytics-tracking-id', DEFAULT_TRACKING_ID);
            ga4.initialize(trackingId);
            setHasInitializedPageTracker(true);
        }
    }, [hasInitializedPageTracker, shouldTrack, env]);

    return /* CanEnableAnalytics() &&*/ showDialog && language && translater ? (
        <CookieView
            translations={generateTranslatedText(manifest, language, translater)}
            onAccept={onAccept}
            onDecline={onDecline}
            privacyPolicyHref={privacyPolicyHref}
        />
    ) : null;
}

export default Analytics;
